/**
 * Google Web Font Catalog — loader and accessors
 *
 * Curated, self-hosted web fonts (WOFF2 on the segmently-assets GCS bucket).
 * A web font is referenced from platformMappings by its catalog id
 * (e.g. "google:inter") — the same slot that otherwise holds a system
 * font name. Resolution stays schema-free: no FontFamily enum change.
 *
 * The JSON catalog is regenerated by scripts/fonts/build-google-font-catalog.ts.
 *
 * @module font-config/data
 */

import type {
  CustomFontDefinition,
  FontCategory,
  ProjectFontConfig,
  WebFontAsset,
} from '../types/fontConfig.types';
import catalogData from './googleFontCatalog.json';

/** Environments that have a segmently-assets-{env} bucket. Local dev uses 'dev'. */
export type WebFontsEnv = 'dev' | 'stage' | 'prod';

/**
 * Curated web font catalog loaded from JSON config.
 * Face URLs contain the literal `{env}` placeholder.
 */
export const GOOGLE_FONT_CATALOG: WebFontAsset[] = (
  catalogData as { fonts: WebFontAsset[] }
).fonts;

const CATALOG_BY_ID = new Map<string, WebFontAsset>(
  GOOGLE_FONT_CATALOG.map((font) => [font.id, font]),
);

/**
 * Find an uploaded custom font by its mapping id ('upload:<fontId>').
 * customFonts is keyed by fontId; tolerate entries keyed by full id too.
 */
function findCustomFont(
  id: string,
  config?: ProjectFontConfig | null,
): CustomFontDefinition | null {
  const customFonts = config?.customFonts;
  if (!customFonts) return null;
  const fontId = id.slice('upload:'.length);
  const entry = customFonts[fontId] ?? customFonts[id];
  if (entry) return entry;
  return Object.values(customFonts).find((font) => font.id === id) ?? null;
}

/**
 * Project the server-produced CustomFontDefinition onto the shared
 * WebFontAsset contract (loader/bake/SSR never see the source).
 * Malformed entries (no faces) resolve to null instead of crashing.
 */
export function customFontToWebFontAsset(
  font: CustomFontDefinition,
): WebFontAsset | null {
  if (!font?.cssFamily || !Array.isArray(font.faces) || font.faces.length === 0) {
    return null;
  }
  return {
    id: font.id,
    family: font.cssFamily,
    category: font.category ?? 'sans-serif',
    source: 'upload',
    license: font.license?.type ?? 'attested',
    weights: font.weights?.length ? font.weights : font.faces.map((f) => f.weight),
    primaryWeight: font.primaryWeight ?? font.faces[0].weight,
    faces: font.faces,
    fallbackMetrics: font.fallbackMetrics ?? {
      ascentOverride: 0.9,
      descentOverride: 0.25,
      lineGapOverride: 0,
      sizeAdjust: 1,
    },
  };
}

/**
 * True when a platformMappings value refers to a web font rather than a
 * system font. Curated ids must exist in the catalog; "upload:*" ids must
 * exist in config.customFonts; anything unknown resolves as a plain
 * (nonsensical) system name, never crashes.
 */
export function isWebFontId(
  value: string | null | undefined,
  config?: ProjectFontConfig | null,
): boolean {
  if (!value) return false;
  if (value.startsWith('google:')) return CATALOG_BY_ID.has(value);
  if (value.startsWith('upload:')) {
    const custom = findCustomFont(value, config);
    return custom ? customFontToWebFontAsset(custom) !== null : false;
  }
  return false;
}

/**
 * Resolve a web-font id (curated or uploaded) to its asset. Returns null
 * for system font names and unknown ids.
 */
export function getWebFont(
  id: string | null | undefined,
  config?: ProjectFontConfig | null,
): WebFontAsset | null {
  if (!id) return null;
  if (id.startsWith('upload:')) {
    const custom = findCustomFont(id, config);
    return custom ? customFontToWebFontAsset(custom) : null;
  }
  return CATALOG_BY_ID.get(id) ?? null;
}

/**
 * Curated options for the admin panel dropdowns, optionally filtered by
 * the slot's category so serif slots offer serif web fonts.
 */
export function getWebFontOptions(category?: FontCategory): WebFontAsset[] {
  if (!category) return GOOGLE_FONT_CATALOG;
  return GOOGLE_FONT_CATALOG.filter((font) => font.category === category);
}

/**
 * Uploaded-font options for the admin panel ("Custom" group), filtered by
 * category like getWebFontOptions.
 */
export function getCustomFontOptions(
  config: ProjectFontConfig | null | undefined,
  category?: FontCategory,
): WebFontAsset[] {
  const assets = Object.values(config?.customFonts ?? {})
    .map(customFontToWebFontAsset)
    .filter((asset): asset is WebFontAsset => asset !== null);
  if (!category) return assets;
  return assets.filter((asset) => asset.category === category);
}

/**
 * Substitute the `{env}` placeholder in every face URL. Pure — returns a
 * new asset; the catalog constant is never mutated.
 */
export function resolveWebFontForEnv(
  asset: WebFontAsset,
  env: WebFontsEnv,
): WebFontAsset {
  return {
    ...asset,
    faces: asset.faces.map((face) => ({
      ...face,
      woff2: face.woff2.replace('{env}', env),
    })),
  };
}

/**
 * The synthetic fallback family name referenced in chains as
 * `'Inter', 'Inter Fallback', sans-serif`. The loader/bake emit an
 * @font-face for it with local() sources + metric overrides.
 */
export function webFontFallbackFamily(family: string): string {
  return `${family} Fallback`;
}

/**
 * local() sources for the metric-adjusted fallback face, per category.
 * Ordered so at least one exists on every platform we target.
 */
export const WEB_FONT_FALLBACK_LOCAL_SOURCES: Record<FontCategory, string[]> = {
  'sans-serif': ['Arial', 'Helvetica Neue', 'Roboto'],
  serif: ['Georgia', 'Times New Roman', 'Noto Serif'],
  monospace: ['Courier New', 'Menlo', 'Roboto Mono'],
};
